import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Buscador, Chip, Filtro, IconoMas, Menu, useBusquedaDiferida, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import {
  useCambiosMarcas,
  useMarcas,
  useModelos,
  useTiposDeActivo,
  type FiltrosMarcas,
  type Marca,
  type Modelo,
} from './datos';
import { VentanaMarca, type Edicion } from './VentanaMarca';
import { VentanaModelo, type EdicionModelo } from './VentanaModelo';

// «Marcas y modelos» (M-7, #61). No hay frame en el Figma: se decidió una sola
// pantalla de dos columnas —marcas a la izquierda, los modelos de la elegida a
// la derecha— porque es una lista de nombres, y abrir una ficha por cada marca
// eran pantallas y toques de más. En el celular las columnas se apilan.
//
// Se agrega y se renombra en una ventana, como «Nueva categoría» de Productos.
// Nada se elimina: los activos ya registrados apuntan a su marca y su modelo, así
// que lo que no se usa más se desactiva y deja de ofrecerse.
//
// Elegir una marca y ver solo sus modelos es el «terminado» del issue: es lo
// mismo que hará el combo del formulario del activo (M-6).

export function ListadoMarcas() {
  const { t } = useTraduccion();
  const cambios = useCambiosMarcas();
  useTituloPantalla(t('admin.marcas.titulo'));

  const [texto, setTexto] = useState('');
  const [filtros, setFiltros] = useState<FiltrosMarcas>({ buscar: '', tipo: 'todos', estado: 'todas' });
  const [elegida, setElegida] = useState<string | null>(null);
  const [edicionMarca, setEdicionMarca] = useState<Edicion>(null);
  const [edicionModelo, setEdicionModelo] = useState<EdicionModelo>(null);
  const { hacer, problema } = useAccionDeFila();

  useBusquedaDiferida(texto, (buscar) => setFiltros((f) => ({ ...f, buscar })), {
    pendiente: texto !== filtros.buscar,
  });

  const tipos = useTiposDeActivo();
  const marcas = useMarcas(filtros);
  const lista = marcas.data ?? [];

  // La elegida se busca en la lista: si un filtro la esconde, la columna de
  // modelos no muestra los de una marca que ya no se ve a la izquierda.
  const marca = lista.find((m) => String(m.id) === elegida) ?? null;
  const modelos = useModelos(marca ? String(marca.id) : null);

  const hayFiltros = filtros.buscar !== '' || filtros.tipo !== 'todos' || filtros.estado !== 'todas';

  function opcionesMarca(m: Marca): ElementoMenu[] {
    const id = String(m.id);
    const nombre = { nombre: m.name };
    return [
      { etiqueta: t('admin.marcas.menu.renombrar'), onSeleccionar: () => setEdicionMarca({ tipo: 'renombrar', marca: m }) },
      m.isActive
        ? {
            etiqueta: t('admin.marcas.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarEstadoMarca(id, false), t('admin.marcas.aviso.marcaDesactivada', nombre)),
          }
        : {
            etiqueta: t('admin.marcas.menu.activar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarEstadoMarca(id, true), t('admin.marcas.aviso.marcaActivada', nombre)),
          },
    ];
  }

  function opcionesModelo(m: Modelo): ElementoMenu[] {
    const id = String(m.id);
    const nombre = { nombre: m.name };
    return [
      {
        etiqueta: t('admin.marcas.menu.renombrar'),
        onSeleccionar: () => setEdicionModelo({ tipo: 'renombrar', modelo: m }),
      },
      m.isActive
        ? {
            etiqueta: t('admin.marcas.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarEstadoModelo(id, false), t('admin.marcas.aviso.modeloDesactivado', nombre)),
          }
        : {
            etiqueta: t('admin.marcas.menu.activar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarEstadoModelo(id, true), t('admin.marcas.aviso.modeloActivado', nombre)),
          },
    ];
  }

  const opcionesTipo = [
    { valor: 'todos', etiqueta: t('admin.marcas.filtro.todos') },
    ...(tipos.data ?? []).map((tipo) => ({ valor: String(tipo.id), etiqueta: tipo.name })),
  ];

  return (
    <>
      <div className="titulo-con-ayuda">
        <h1>{t('admin.marcas.titulo')}</h1>
        <Ayuda tema={t('admin.marcas.titulo')} texto={t('admin.marcas.info')} tono="titulo" />
      </div>

      {problema && <AvisoError aviso={{ titulo: t('admin.marcas.error.titulo'), texto: problema }} />}

      <div className="marcas">
        <section className="marcas__columna" aria-labelledby="marcas-titulo">
          <header className="marcas__cabecera">
            <h2 id="marcas-titulo">{t('admin.marcas.columnaMarcas')}</h2>
            <Boton icono={<IconoMas />} onClick={() => setEdicionMarca({ tipo: 'nueva' })}>
              {t('admin.marcas.nuevaMarca')}
            </Boton>
          </header>

          <div className="marcas__filtros">
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.marcas.buscar')} />
            <Filtro
              etiqueta={t('admin.marcas.filtro.tipo')}
              valor={filtros.tipo}
              onCambiar={(tipo) => setFiltros((f) => ({ ...f, tipo }))}
              opciones={opcionesTipo}
            />
            <Filtro
              etiqueta={t('admin.marcas.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(estado) => setFiltros((f) => ({ ...f, estado: estado as FiltrosMarcas['estado'] }))}
              opciones={[
                { valor: 'todas', etiqueta: t('admin.marcas.filtro.todas') },
                { valor: 'activas', etiqueta: t('admin.marcas.activa') },
                { valor: 'inactivas', etiqueta: t('admin.marcas.inactiva') },
              ]}
            />
          </div>

          {marcas.isPending ? (
            <p className="marcas__nota">{t('app.cargando')}</p>
          ) : marcas.isError ? (
            <div className="marcas__nota">
              <p>{t('ui.listado.error.texto')}</p>
              <Boton variante="secundario" onClick={() => void marcas.refetch()}>
                {t('app.reintentar')}
              </Boton>
            </div>
          ) : lista.length === 0 ? (
            <div className="marcas__nota">
              <p className="marcas__nota-titulo">
                {hayFiltros ? t('ui.listado.sinResultados.titulo') : t('admin.marcas.vacio.titulo')}
              </p>
              <p>{hayFiltros ? t('ui.listado.sinResultados.texto') : t('admin.marcas.vacio.texto')}</p>
              {hayFiltros && (
                <Boton
                  variante="secundario"
                  onClick={() => {
                    setTexto('');
                    setFiltros({ buscar: '', tipo: 'todos', estado: 'todas' });
                  }}
                >
                  {t('ui.listado.limpiarFiltros')}
                </Boton>
              )}
            </div>
          ) : (
            <ul className="marcas__lista">
              {lista.map((m) => (
                <li key={String(m.id)} className={`marcas__fila${marca?.id === m.id ? ' marcas__fila--elegida' : ''}`}>
                  <button
                    type="button"
                    className="marcas__elegir"
                    aria-pressed={marca?.id === m.id}
                    onClick={() => setElegida(String(m.id))}
                  >
                    <span className="marcas__nombre">{m.name}</span>
                    <span className="marcas__detalle">
                      <Chip tono={m.assetTypeName ? 'marca' : 'neutro'}>
                        {m.assetTypeName ?? t('admin.marcas.tipo.generica')}
                      </Chip>
                      {!m.isActive && <Chip>{t('admin.marcas.inactiva')}</Chip>}
                      <span className="marcas__conteo">{t('admin.marcas.conteoModelos', { n: m.modelCount })}</span>
                    </span>
                  </button>
                  <MenuFila elementos={opcionesMarca(m)} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="marcas__columna" aria-labelledby="modelos-titulo">
          <header className="marcas__cabecera">
            <h2 id="modelos-titulo">
              {marca ? t('admin.marcas.columnaModelosDe', { marca: marca.name }) : t('admin.marcas.columnaModelos')}
            </h2>
            <Boton
              icono={<IconoMas />}
              disabled={marca === null}
              onClick={() => marca && setEdicionModelo({ tipo: 'nuevo', marca })}
            >
              {t('admin.marcas.nuevoModelo')}
            </Boton>
          </header>

          {marca === null ? (
            <p className="marcas__nota">{t('admin.marcas.eligeUnaMarca')}</p>
          ) : modelos.isPending ? (
            <p className="marcas__nota">{t('app.cargando')}</p>
          ) : modelos.isError ? (
            <div className="marcas__nota">
              <p>{t('ui.listado.error.texto')}</p>
              <Boton variante="secundario" onClick={() => void modelos.refetch()}>
                {t('app.reintentar')}
              </Boton>
            </div>
          ) : modelos.data.length === 0 ? (
            <p className="marcas__nota">{t('admin.marcas.sinModelos', { marca: marca.name })}</p>
          ) : (
            <ul className="marcas__lista">
              {modelos.data.map((m) => (
                <li key={String(m.id)} className="marcas__fila">
                  <span className="marcas__elegir marcas__elegir--fijo">
                    <span className="marcas__nombre">{m.name}</span>
                    {!m.isActive && (
                      <span className="marcas__detalle">
                        <Chip>{t('admin.marcas.inactivo')}</Chip>
                      </span>
                    )}
                  </span>
                  <MenuFila elementos={opcionesModelo(m)} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <VentanaMarca edicion={edicionMarca} onCerrar={() => setEdicionMarca(null)} onCreada={setElegida} />
      <VentanaModelo edicion={edicionModelo} onCerrar={() => setEdicionModelo(null)} />
    </>
  );
}

/** El «•••» de cada fila, el mismo del listado. */
function MenuFila({ elementos }: { elementos: readonly ElementoMenu[] }) {
  const { t } = useTraduccion();
  return (
    <Menu
      elementos={elementos}
      disparador={({ alternar, atributos }) => (
        <button
          type="button"
          className="ui-tabla__mas"
          aria-label={t('ui.listado.accionesFila')}
          onClick={alternar}
          {...atributos}
        >
          •••
        </button>
      )}
    />
  );
}
